import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

import axios from 'axios';
import PropTypes from 'prop-types';

const AuthContext = createContext({ isAuthenticated: false });

// Module-level state so login/logout can be called outside React components
// (e.g. in axios interceptors)
let _setAuthState = null;

/**
 * Sets the authenticated state. Can be called outside React component tree.
 */
export function login() {
  if (_setAuthState) _setAuthState(true);
}

/**
 * Clears the authenticated state. Can be called outside React component tree.
 * The actual cookie is cleared server-side by the logout endpoint.
 */
export function logout() {
  if (_setAuthState) _setAuthState(false);
}

/**
 * Hook to get the current authentication state.
 * Returns [isAuthenticated] to maintain API compatibility with react-token-auth.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  return [context.isAuthenticated];
}

/**
 * AuthProvider component — wraps the app and provides authentication state.
 * On mount, calls /api/auth/check to verify if a valid JWT cookie exists.
 * Returns null during the brief auth check — App.js handles the visible loading screen.
 */
export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const updateAuth = useCallback((value) => {
    setIsAuthenticated(value);
  }, []);

  useEffect(() => {
    _setAuthState = updateAuth;

    // Check if a valid JWT cookie exists on mount
    axios
      .get(`${process.env.REACT_APP_BACK_URL}/api/auth/check`, {
        withCredentials: true,
      })
      .then(() => {
        setIsAuthenticated(true);
      })
      .catch(() => {
        setIsAuthenticated(false);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      _setAuthState = null;
    };
  }, [updateAuth]);

  const contextValue = useMemo(() => ({ isAuthenticated }), [isAuthenticated]);

  if (isLoading) {
    return null;
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
